import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Container,
 
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
  RadialLinearScale,
} from "chart.js";
import {  Bar, Doughnut } from "react-chartjs-2";
import {
  TrendingUp,
  
  AttachMoney as DollarSign,
  TrackChanges as Target,
  
  Assessment,
} from "@mui/icons-material";
import Layout from "../../components/layout/ChartsLayout";
import config from "../../config";


// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
  RadialLinearScale
);

const theme = createTheme({
  palette: {
    primary: {
      main: "#EB5A3C",
      light: "#FF7A5C",
      dark: "#C54428",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#F8EEDB",
      light: "#FFFAF0",
      dark: "#E6D4B7",
      contrastText: "#333333",
    },
    background: {
      default: "#F8EEDB",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#333333",
      secondary: "#666666",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: "0 4px 20px rgba(235, 90, 60, 0.1)",
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 8px 30px rgba(235, 90, 60, 0.15)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
  },
});

const PartnersCapital = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { API_BASE_URL } = config;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${API_BASE_URL}/api/v1/getFinancialReports`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          setData(result);
          setError(null);
        } else {
          throw new Error(result.message || "Failed to fetch data");
        }
      } catch (err) {
        console.error("Error fetching financial data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API_BASE_URL]);

  const excelDateToJS = (serial) => {
    if (!serial) return "N/A";
    const excelStartDate = new Date(1900, 0, 1);
    const jsDate = new Date(
      excelStartDate.getTime() + (serial - 2) * 24 * 60 * 60 * 1000
    );
    return jsDate.toLocaleDateString();
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "$0";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return "0%";
    return `${(value * 100).toFixed(1)}%`;
  };

  if (loading) {
    return (
      <Layout>
        <ThemeProvider theme={theme}>
          <Box
            sx={{
              minHeight: "100vh",
              background: "linear-gradient(135deg, #F8EEDB 0%, #FFFAF0 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  textAlign: "center",
                  borderRadius: 4,
                }}
              >
                <CircularProgress size={60} sx={{ color: "#EB5A3C", mb: 2 }} />
                <Typography variant="h6" color="text.primary">
                  Loading Investment Data...
                </Typography>
              </Paper>
            </motion.div>
          </Box>
        </ThemeProvider>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <ThemeProvider theme={theme}>
          <Box
            sx={{
              minHeight: "100vh",
              background: "linear-gradient(135deg, #F8EEDB 0%, #FFFAF0 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Alert
                severity="error"
                sx={{
                  maxWidth: 600,
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Error loading investment data
                </Typography>
                <Typography variant="body2">{error}</Typography>
              </Alert>
            </motion.div>
          </Box>
        </ThemeProvider>
      </Layout>
    );
  }

  if (!data?.success || !data?.data?.reports?.Investment_Sedule) {
    return (
      <Layout>
        <ThemeProvider theme={theme}>
          <Box
            sx={{
              minHeight: "100vh",
              background: "linear-gradient(135deg, #F8EEDB 0%, #FFFAF0 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Alert severity="warning">No investment data available</Alert>
          </Box>
        </ThemeProvider>
      </Layout>
    );
  }

  const investmentData = data.data.reports.Investment_Sedule?.data?.Sheet1 || [];

  // Calculate summary statistics
  const totalInvestment = investmentData.reduce((sum, inv) => sum + (inv["Investment Amount"] || 0), 0);
  const totalExpectedValue = investmentData.reduce((sum, inv) => sum + (inv["Expected Maturity Value"] || 0), 0);
  const averageReturn = investmentData.reduce((sum, inv) => sum + (inv["Expected Annual Return (%)"] || 0), 0) / investmentData.length;
  const totalExpectedProfit = totalExpectedValue - totalInvestment;

  // Prepare chart data
  const investmentAmountChart = {
    labels: investmentData.map(inv => inv["Investment Name"]),
    datasets: [
      {
        label: "Investment Amount",
        data: investmentData.map(inv => inv["Investment Amount"]),
        backgroundColor: "rgba(235, 90, 60, 0.6)",
        borderColor: "#EB5A3C",
        borderWidth: 2,
      }
    ]
  };

  const returnComparisonChart = {
    labels: investmentData.map(inv => inv["Investment Name"]),
    datasets: [
      {
        label: "Investment Amount",
        data: investmentData.map(inv => inv["Investment Amount"]),
        backgroundColor: "rgba(235, 90, 60, 0.6)",
        borderColor: "#EB5A3C",
        borderWidth: 2,
      },
      {
        label: "Expected Maturity Value",
        data: investmentData.map(inv => inv["Expected Maturity Value"]),
        backgroundColor: "rgba(72, 187, 120, 0.6)",
        borderColor: "#48BB78",
        borderWidth: 2,
      }
    ]
  };

  const returnRateChart = {
    labels: investmentData.map(inv => inv["Investment Name"]),
    datasets: [
      {
        label: "Expected Annual Return (%)",
        data: investmentData.map(inv => (inv["Expected Annual Return (%)"] * 100)),
        backgroundColor: investmentData.map((_, index) => 
          `hsl(${(index * 137.5) % 360}, 70%, 60%)`
        ),
        borderWidth: 0,
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'right',
      },
      title: {
        display: false,
      },
    },
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getMaturityStatus = (maturityDate) => {
    const today = new Date();
    const maturity = new Date(1900, 0, 1);
    maturity.setTime(maturity.getTime() + (maturityDate - 2) * 24 * 60 * 60 * 1000);
    
    if (maturity < today) {
      return { status: "Matured", color: "success" };
    } else if (maturity <= new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000)) {
      return { status: "Maturing Soon", color: "warning" };
    } else {
      return { status: "Active", color: "primary" };
    }
  };

  return (
    <Layout>
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            minHeight: "100vh",
            background: "linear-gradient(135deg, #F8EEDB 0%, #FFFAF0 100%)",
            py: 3,
          }}
        >
          <Container maxWidth="xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Header */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h4" color="primary" gutterBottom>
                  Partners Capital Dashboard
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Investment portfolio overview and performance analytics
                </Typography>
              </Box>

              {/* Summary Cards */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <motion.div whileHover={{ scale: 1.02 }}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                          <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>
                            <DollarSign />
                          </Avatar>
                          <Typography variant="h6" color="text.primary">
                            Total Investment
                          </Typography>
                        </Box>
                        <Typography variant="h4" color="primary.main" fontWeight="bold">
                          {formatCurrency(totalInvestment)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <motion.div whileHover={{ scale: 1.02 }}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                          <Avatar sx={{ bgcolor: "success.main", mr: 2 }}>
                            <Target />
                          </Avatar>
                          <Typography variant="h6" color="text.primary">
                            Expected Value
                          </Typography>
                        </Box>
                        <Typography variant="h4" color="success.main" fontWeight="bold">
                          {formatCurrency(totalExpectedValue)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <motion.div whileHover={{ scale: 1.02 }}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                          <Avatar sx={{ bgcolor: "warning.main", mr: 2 }}>
                            <TrendingUp />
                          </Avatar>
                          <Typography variant="h6" color="text.primary">
                            Expected Profit
                          </Typography>
                        </Box>
                        <Typography variant="h4" color="warning.main" fontWeight="bold">
                          {formatCurrency(totalExpectedProfit)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <motion.div whileHover={{ scale: 1.02 }}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                          <Avatar sx={{ bgcolor: "info.main", mr: 2 }}>
                            <Assessment />
                          </Avatar>
                          <Typography variant="h6" color="text.primary">
                            Avg. Return
                          </Typography>
                        </Box>
                        <Typography variant="h4" color="info.main" fontWeight="bold">
                          {formatPercent(averageReturn)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              </Grid>

              {/* Tabs */}
              <Paper sx={{ mb: 3 }}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant="fullWidth"
                  sx={{
                    "& .MuiTab-root": {
                      fontWeight: 600,
                    },
                  }}
                >
                  <Tab label="Overview" />
                  <Tab label="Performance Charts" />
                  <Tab label="Investment Details" />
                </Tabs>
              </Paper>

              {/* Tab Content */}
              <AnimatePresence mode="wait">
                {activeTab === 0 && (
                  <motion.div
                    key="overview"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={8}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" gutterBottom>
                              Investment vs Expected Returns
                            </Typography>
                            <Bar data={returnComparisonChart} options={chartOptions} />
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Card sx={{ height: "100%" }}>
                          <CardContent>
                            <Typography variant="h6" gutterBottom>
                              Return Rate Distribution
                            </Typography>
                            <Doughnut data={returnRateChart} options={doughnutOptions} />
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </motion.div>
                )}

                {activeTab === 1 && (
                  <motion.div
                    key="performance"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" gutterBottom>
                              Investment Amounts by Project
                            </Typography>
                            <Bar data={investmentAmountChart} options={chartOptions} />
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </motion.div>
                )}

                {activeTab === 2 && (
                  <motion.div
                    key="details"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Investment Portfolio Details
                        </Typography>
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>Investment Name</strong></TableCell>
                                <TableCell><strong>Investment Date</strong></TableCell>
                                <TableCell><strong>Maturity Date</strong></TableCell>
                                <TableCell><strong>Amount</strong></TableCell>
                                <TableCell><strong>Annual Return</strong></TableCell>
                                <TableCell><strong>Expected Value</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {investmentData.map((investment, index) => {
                                const maturityStatus = getMaturityStatus(investment["Maturity Date"]);
                                return (
                                  <TableRow key={index} hover>
                                    <TableCell>
                                      <Typography variant="body2" fontWeight="medium">
                                        {investment["Investment Name"]}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      {excelDateToJS(investment["Investment Date"])}
                                    </TableCell>
                                    <TableCell>
                                      {excelDateToJS(investment["Maturity Date"])}
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" color="primary" fontWeight="medium">
                                        {formatCurrency(investment["Investment Amount"])}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" color="success.main" fontWeight="medium">
                                        {formatPercent(investment["Expected Annual Return (%)"])}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" color="success.main" fontWeight="medium">
                                        {formatCurrency(investment["Expected Maturity Value"])}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={maturityStatus.status}
                                        color={maturityStatus.color}
                                        size="small"
                                      />
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </Container>
        </Box>
      </ThemeProvider>
    </Layout>
  );
};

export default PartnersCapital;
